import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Design system
import type { SelectOptionPropsType } from '@Shelly/shelly-design-system/index';
//Helpers
import { devices_settings } from 'src/stores/device-runtime/status';
import { capFirstChar } from 'src/helpers/utilities';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob, LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';

export const CAMERA_DURATION_MIN = 1;
export const CAMERA_DURATION_MAX = 3600;
export const CAMERA_DURATION_DEFAULT = 30;

export const isCameraLocalActionMethod = (method: string): boolean => {
	return /^camera\.(captureimage|startrecording|setconfig)$/i.test(method?.split(':')[0] || '');
};

export const getCameraLocalActions = (
	action: ActionJob,
	device: Device,
	params: { id: number },
): LocalControlOptions[] => {
	const $format = get(format);
	const target = getTargetOptions(device);

	return [
		{
			title: $format('local_action_camera_capture_image', {
				default: 'Capture Image',
			}),
			value: 'camera.captureimage',
			attr: { target },
			params,
		},
		{
			title: $format('local_action_camera_start_recording', {
				default: 'Start Recording',
			}),
			value: 'camera.startrecording',
			attr: {
				target,
				duration: action.params?.duration != null,
			},
			params: {
				...params,
				duration: action.params?.duration ?? CAMERA_DURATION_DEFAULT,
			},
		},
		{
			title: $format('local_action_camera_configure', {
				default: 'Configure Camera',
			}),
			value: 'camera.setconfig',
			attr: {
				target,
				config: {
					arm: action.params?.config?.arm != null,
					privacy: action.params?.config?.privacy != null,
				},
			},
			params: {
				...params,
				config: {
					arm: action.params?.config?.arm || false,
					privacy: action.params?.config?.privacy || false,
				},
			},
		},
	];
};

const getTargetOptions = (device: Device): SelectOptionPropsType[] => {
	const $format = get(format);
	const channelsOptions: SelectOptionPropsType[] = [];
	const deviceSettings = devices_settings.getByRealId(device.id) || {};
	Object.keys(deviceSettings).forEach((key) => {
		if (key.startsWith('camera:')) {
			const cid = deviceSettings[key]?.id ?? 0;
			channelsOptions.push({
				title:
					deviceSettings[key]?.name ||
					$format('webhooks_component_camera', {
						values: { id: cid },
						default: `Camera (${cid})`,
					}),
				value: cid,
			});
		}
	});
	return channelsOptions;
};

export const getCameraComponents = (deviceSetting: any) => {
	const options = [];
	const components = Object.keys(deviceSetting || {})
		.filter((i) => /^(camera|camerazone):\d/.test(i))
		.sort();
	for (let i = 0; i < components.length; ++i) {
		const type = components[i].split(':')[0];
		const component = deviceSetting[components[i]];
		const cid = component.id ?? Number(components[i].split(':')[1]) ?? 0;
		options.push({
			title: capFirstChar(component.name || `${type} (${component.id})`),
			type,
			component: components[i],
			inputType: type,
			cid,
		});
	}
	return options;
};
